import React, { useEffect, useReducer } from "react";
import { fetchUserLocation, getCurrentUser } from "./actions/auth";
import { loadCountries } from "./actions/countries";
import { safeGet, safeRemove, safeSet } from "../utils/safeStorage";
import {
  FetchallLiveCousrse,
  FetchallOnlineCousrse,
  FetchCoursesCategory,
} from "./actions/courses";
import authReducer, { defaultAuth } from "./reducers.tsx/authReducer";
import genericReducer, { defaultData } from "./reducers.tsx/genericReducer";

const genericData = {
  error: null,
  loading: false,
  data: [],
};
const locationData = {
  error: null,
  loading: false,
  data: {},
};

export const GlobalContext = React.createContext({
  authState: defaultAuth,
  authDispatch: () => {},
  countryCodeState: genericData,
  countryCodeDispatch: () => {},
  onlineCoursesState: defaultData,
  onlineCoursesDispatch: () => {},
  liveCoursesState: defaultData,
  liveCoursesDispatch: () => {},
  userCoursesState: defaultData,
  userCoursesDispatch: () => {},
  categoryState: defaultData,
  categoryDispatch: () => {},
  userLocation: locationData,
  userLocationDispatch: () => {},
  communityState: defaultData,
  communityDispatch: () => {},
  scheduleState: defaultData,
  scheduleDispatch: () => {},
});

function getInitialState(key, defaultState) {
  const storedState = safeGet(key);
  if (!storedState) return defaultState;
  let parsed;
  try {
    parsed = JSON.parse(storedState);
  } catch {
    // Corrupt saved state (e.g. a write cut off by a full quota) — start fresh
    // instead of crashing the app on this device on every load.
    safeRemove(key);
    return defaultState;
  }
  if (!parsed) return defaultState;
  // If this state expects an array but localStorage has an object, normalize it
  if (Array.isArray(defaultState.data) && parsed.data && !Array.isArray(parsed.data)) {
    return { ...parsed, data: Array.isArray(parsed.data?.data) ? parsed.data.data : [] };
  }
  return parsed;
}

export default function GlobalProvider({ children }) {
  const [authState, authDispatch] = useReducer(
    authReducer,
    getInitialState("authState", defaultAuth)
  );
  const [countryCodeState, countryCodeDispatch] = useReducer(
    genericReducer,
    getInitialState("countryCodeState", genericData)
  );
  const [scheduleState, scheduleDispatch] = useReducer(
    genericReducer,
    getInitialState("scheduleState", genericData)
  );
  const [communityState, communityDispatch] = useReducer(
    genericReducer,
    getInitialState("communityState", genericData)
  );
  const [categoryState, categoryDispatch] = useReducer(
    genericReducer,
    getInitialState("categoryState", genericData)
  );
  const [onlineCoursesState, onlineCoursesDispatch] = useReducer(
    genericReducer,
    getInitialState("onlineCoursesState", genericData)
  );
  const [liveCoursesState, liveCoursesDispatch] = useReducer(
    genericReducer,
    getInitialState("liveCoursesState", genericData)
  );
  const [userCoursesState, userCoursesDispatch] = useReducer(
    genericReducer,
    getInitialState("userCoursesState", genericData)
  );
  const [userLocation, userLocationDispatch] = useReducer(
    genericReducer,
    getInitialState("userLocation", locationData)
  );

  useEffect(() => {
    // Fetch initial data
    fetchUserLocation(userLocationDispatch);
    loadCountries(countryCodeDispatch);
    getCurrentUser(authDispatch);
    FetchallLiveCousrse(liveCoursesDispatch);
    FetchallOnlineCousrse(onlineCoursesDispatch);
    FetchCoursesCategory(categoryDispatch);
  }, []);

  useEffect(() => {
    if (authState?.data?.user?.location) {
      userLocationDispatch({
        type: "FETCHED_DATA",
        payload: authState.data.user.location,
      });
    }
  }, [authState?.data?.user?.location]);

  // Save states to localStorage whenever they change
  useEffect(() => {
    safeSet("authState", JSON.stringify(authState));
  }, [authState]);

  useEffect(() => {
    safeSet("countryCodeState", JSON.stringify(countryCodeState));
  }, [countryCodeState]);

  useEffect(() => {
    safeSet("communityState", JSON.stringify(communityState));
  }, [communityState]);

  useEffect(() => {
    safeSet("categoryState", JSON.stringify(categoryState));
  }, [categoryState]);
  useEffect(() => {
    safeSet("categoryState", JSON.stringify(categoryState));
  }, [categoryState]);

  useEffect(() => {
    safeSet(
      "onlineCoursesState",
      JSON.stringify(onlineCoursesState)
    );
  }, [onlineCoursesState]);

  useEffect(() => {
    safeSet("liveCoursesState", JSON.stringify(liveCoursesState));
  }, [liveCoursesState]);

  useEffect(() => {
    safeSet("scheduleState", JSON.stringify(scheduleState));
  }, [scheduleState]);

  useEffect(() => {
    safeSet("userLocation", JSON.stringify(userLocation));
  }, [userLocation]);

  return (
    <GlobalContext.Provider
      value={{
        authState,
        authDispatch,
        countryCodeState,
        countryCodeDispatch,
        onlineCoursesState,
        onlineCoursesDispatch,
        liveCoursesState,
        liveCoursesDispatch,
        userCoursesState,
        userCoursesDispatch,
        userLocation,
        userLocationDispatch,
        communityState,
        communityDispatch,
        categoryState,
        categoryDispatch,
        scheduleDispatch,
        scheduleState,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
}
